// probably timing problem with lit: https://github.com/lit/lit/blob/7453e365000e6a289c139cf7e175a4742296333d/packages/reactive-element/src/css-tag.ts#L10
// if lit is loaded before style sheet it cannot handle CSSStyleSheets from AdornisCSS
import 'construct-style-sheets-polyfill'; // THIS HAS TO BE THE FIRST IMPORT FOR FIREFOX TO WORK CORRECTLY

import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import '../tailwind.css' with { type: 'tailwind' };

// Storage key for localStorage
const STORAGE_KEY_HISTORY = 'embed-tester-history';

// Types for our history items
interface HistoryItem {
  id: string;
  headHtml: string;
  bodyHtml: string;
  timestamp: number;
  title: string;
}

// Helper functions for history management
const getHistory = (): HistoryItem[] => {
  try {
    const historyJson = localStorage.getItem(STORAGE_KEY_HISTORY);
    return historyJson ? JSON.parse(historyJson) : [];
  } catch (error) {
    console.error('Error reading history from localStorage:', error);
    return [];
  }
};

const saveHistory = (history: HistoryItem[]) => {
  try {
    localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(history));
  } catch (error) {
    console.error('Error saving history to localStorage:', error);
  }
};

const addToHistory = (headHtml: string, bodyHtml: string) => {
  if (!headHtml && !bodyHtml) return;

  const history = getHistory();

  // Generate a simple title from the content
  let title = '';
  if (bodyHtml) {
    title = bodyHtml.substring(0, 30) + (bodyHtml.length > 30 ? '...' : '');
  } else if (headHtml) {
    // Extract title from head HTML if it contains a title tag
    const titleMatch = headHtml.match(/<title>([^<]+)<\/title>/i);
    if (titleMatch && titleMatch[1]) {
      title = titleMatch[1];
    } else {
      title = headHtml.substring(0, 30) + (headHtml.length > 30 ? '...' : '');
    }
  }

  // Check if this entry already exists in history (deduplicate)
  const existingIndex = history.findIndex(item => item.headHtml === headHtml && item.bodyHtml === bodyHtml);

  // If the entry exists, remove it from its current position
  if (existingIndex !== -1) {
    history.splice(existingIndex, 1);
  }

  // Create a new history item
  const newItem: HistoryItem = {
    id: Date.now().toString(),
    headHtml,
    bodyHtml,
    timestamp: Date.now(),
    title,
  };

  // Add to the beginning of the array
  history.unshift(newItem);

  // Keep only the last 5 items
  const trimmedHistory = history.slice(0, 5);

  // Save back to localStorage
  saveHistory(trimmedHistory);

  return trimmedHistory;
};

// Create the embed tester component
const EmbedTester = stateful(() => {
  // Get history from localStorage
  const initialHistory = getHistory();
  const [history, setHistory] = useState<HistoryItem[]>(initialHistory);

  // Initialize with the first history item if available, otherwise empty strings
  const initialHeadHtml = initialHistory.length > 0 ? initialHistory[0]?.headHtml || '' : '';
  const initialBodyHtml = initialHistory.length > 0 ? initialHistory[0]?.bodyHtml || '' : '';

  const [headHtml, setHeadHtml] = useState(initialHeadHtml);
  const [bodyHtml, setBodyHtml] = useState(initialBodyHtml);
  const [isLoading, setIsLoading] = useState(false);

  // Create a ref for the form
  const formRef = createRef<HTMLFormElement>();

  // Update head HTML state
  const updateHeadHtml = (e: Event) => {
    const value = (e.target as HTMLTextAreaElement).value;
    setHeadHtml(value);
  };

  // Update body HTML state
  const updateBodyHtml = (e: Event) => {
    const value = (e.target as HTMLTextAreaElement).value;
    setBodyHtml(value);
  };

  // Handle form submission to save to history
  const handleSubmit = (e: Event) => {
    // Add current values to history
    const updatedHistory = addToHistory(headHtml, bodyHtml);
    if (updatedHistory) {
      setHistory(updatedHistory);
    }

    // Set loading state
    setIsLoading(true);

    // Continue with normal form submission
    return true;
  };

  // Load a history item into the form
  const loadHistoryItem = (item: HistoryItem) => {
    setHeadHtml(item.headHtml);
    setBodyHtml(item.bodyHtml);
  };

  return html`
    <div class="h-full bg-base-100 p-6 overflow-y-auto">
      ${isLoading
        ? html`
            <div class="fixed inset-0 bg-base-300/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div class="flex flex-col items-center gap-4">
                <span class="loading loading-spinner loading-lg text-primary"></span>
                <p class="text-base-content font-medium">Applying embeds...</p>
              </div>
            </div>
          `
        : ''}
      <div class="max-w-4xl mx-auto">
        <h1 class="text-3xl font-bold mb-6 text-base-content">Embed Tester</h1>

        ${history.length > 0
          ? html`
              <div class="mb-8">
                <h2 class="text-xl font-semibold mb-4 text-base-content">Recent Embeds</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  ${history.map(
                    item => html`
                      <div
                        class="card bg-base-200 shadow-sm hover:shadow-md cursor-pointer transition-all duration-200 hover:bg-primary/10 hover:scale-105 hover:-translate-y-1"
                        @click=${() => loadHistoryItem(item)}
                      >
                        <div class="card-body p-4">
                          <h3 class="card-title text-sm font-medium text-base-content">${item.title}</h3>
                          <p class="text-xs text-base-content/60">${new Date(item.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    `,
                  )}
                </div>
              </div>
            `
          : ''}

        <form ${ref(formRef)} action="/embed-preview" method="post" class="w-full" @submit=${handleSubmit}>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div class="card bg-base-200 shadow-xl">
              <div class="card-body">
                <h2 class="card-title text-base-content">Head Embed</h2>
                <p class="text-base-content/70 text-sm mb-4">Enter HTML to be embedded in the head section</p>
                <textarea
                  name="headHtml"
                  class="textarea textarea-bordered h-64 w-full"
                  placeholder="<script>...</script> or <style>...</style> or <meta>..."
                  .value=${headHtml}
                  @input=${updateHeadHtml}
                ></textarea>
              </div>
            </div>

            <div class="card bg-base-200 shadow-xl">
              <div class="card-body">
                <h2 class="card-title text-base-content">Body Embed</h2>
                <p class="text-base-content/70 text-sm mb-4">Enter HTML to be embedded in the body section</p>
                <textarea
                  name="bodyHtml"
                  class="textarea textarea-bordered h-64 w-full"
                  placeholder="<div>...</div> or <custom-element>...</custom-element>"
                  .value=${bodyHtml}
                  @input=${updateBodyHtml}
                ></textarea>
              </div>
            </div>
          </div>

          <div class="flex justify-center gap-4 mb-8">
            <button type="submit" class="btn btn-primary">Apply Embeds</button>
          </div>
        </form>
      </div>
    </div>
  `;
});

const router = makeRouter({
  embedTester: {
    path: '/',
    render: EmbedTester,
  },
}).init();

initApp(() => html`${router.outlet.value}`);
